import { useRef, useState } from 'react';
import { Image as ImageIcon, Info, Upload } from 'lucide-react';
import { uploadImageToCloudinary } from '../../utils/uploadCloudinary';
import {
  CAMPAIGN_IN_APP_IMAGE_FORMAT_HINT_BODY,
  CAMPAIGN_IN_APP_IMAGE_FORMAT_HINT_TITLE,
} from './campaignImageFormatHint';
import StorefrontFocalAdjuster from './StorefrontFocalAdjuster';
import {
  DEFAULT_STOREFRONT_OBJECT_POSITION,
  sanitizeStorefrontObjectPosition,
} from './storefrontObjectPosition';

/**
 * Image campagne — même pile Cloudinary que logo / produits (uploadImageToCloudinary).
 * Zone cliquable + drag & drop (alignée ProduitImageUploader / GiftCard).
 * Recommandation format In-App = aide UX uniquement (aucune validation dimensionnelle).
 */
export default function CampaignImageField({
  imageUrl,
  onChange,
  showInAppFormatHint = false,
  showStorefrontFocal = false,
  storefrontObjectPosition,
  onStorefrontObjectPositionChange,
}) {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [localError, setLocalError] = useState('');
  const inputRef = useRef(null);
  const position = sanitizeStorefrontObjectPosition(storefrontObjectPosition);

  const uploadOne = async (file) => {
    if (!file) return;
    if (!String(file.type || '').startsWith('image/')) {
      setLocalError('Fichier non image : choisissez un JPG, PNG ou WebP.');
      return;
    }
    try {
      setLocalError('');
      setUploading(true);
      const url = await uploadImageToCloudinary(file, 'growth-campaigns');
      onChange(url);
      if (showStorefrontFocal && onStorefrontObjectPositionChange) {
        onStorefrontObjectPositionChange({ ...DEFAULT_STOREFRONT_OBJECT_POSITION });
      }
    } catch (err) {
      console.error('[CampaignImageField]', err);
      setLocalError('Erreur lors de l’upload de l’image. Réessayez.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleFileInput = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadOne(file);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (uploading) return;
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (uploading) return;
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    await uploadOne(file);
  };

  const openPicker = () => {
    if (uploading) return;
    inputRef.current?.click();
  };

  return (
    <div data-testid="campaign-image-field">
      <div className="flex items-center justify-between gap-2 mb-2">
        <label className="block text-sm font-medium text-gray-700">
          Image (optionnel)
          {uploading && (
            <span className="ml-2 text-xs font-normal text-blue-600">Envoi…</span>
          )}
        </label>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className={`text-sm font-medium text-primary-700 hover:text-primary-800 ${
              uploading ? 'opacity-50 pointer-events-none' : ''
            }`}
            disabled={uploading}
            onClick={openPicker}
            data-testid="campaign-image-pick"
          >
            {imageUrl ? 'Remplacer' : 'Ajouter une image'}
          </button>
          {imageUrl && (
            <button
              type="button"
              className="text-sm text-red-600 hover:text-red-800"
              disabled={uploading}
              onClick={() => {
                setLocalError('');
                onChange('');
              }}
              data-testid="campaign-image-clear"
            >
              Supprimer
            </button>
          )}
        </div>
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        disabled={uploading}
        onChange={handleFileInput}
        data-testid="campaign-image-input"
      />

      {showInAppFormatHint ? (
        <div
          className="mb-3 flex items-start gap-2 rounded-md border border-gray-100 bg-gray-50/80 px-3 py-2 text-xs text-gray-600"
          data-testid="campaign-in-app-image-format-hint"
        >
          <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0 text-indigo-500" aria-hidden />
          <p>
            <span className="font-medium text-gray-700">{CAMPAIGN_IN_APP_IMAGE_FORMAT_HINT_TITLE}</span>
            <br />
            {CAMPAIGN_IN_APP_IMAGE_FORMAT_HINT_BODY}
          </p>
        </div>
      ) : null}

      <div
        role="button"
        tabIndex={0}
        data-testid="campaign-image-dropzone"
        aria-label="Zone image : cliquer ou déposer un fichier"
        className={`rounded-lg border-2 border-dashed px-4 py-6 text-center transition ${
          dragActive
            ? 'border-primary-500 bg-primary-50'
            : 'border-gray-200 bg-gray-50/50'
        } ${uploading ? 'opacity-60 pointer-events-none' : 'cursor-pointer'}`}
        onClick={openPicker}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openPicker();
          }
        }}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
      >
        {imageUrl ? (
          <div
            className="rounded-lg overflow-hidden border border-gray-200 bg-gray-50"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={imageUrl}
              alt="Image de campagne"
              className="w-full max-h-48 object-cover"
              data-testid="campaign-image-preview"
            />
            <p className="py-2 text-xs text-gray-500">
              Cliquez « Remplacer » ou déposez une nouvelle image ici
            </p>
          </div>
        ) : (
          <>
            {uploading ? (
              <Upload className="mx-auto h-8 w-8 text-primary-500 animate-pulse" />
            ) : (
              <ImageIcon className="mx-auto h-8 w-8 text-gray-400" />
            )}
            <p className="mt-2 text-sm text-gray-600">
              Cliquez ou glissez-déposez une image ici
            </p>
            <p className="mt-1 text-xs text-gray-400">JPG, PNG ou WebP — sans saisie d’URL</p>
          </>
        )}
      </div>

      {localError ? (
        <p className="mt-2 text-sm text-red-600" data-testid="campaign-image-error" role="alert">
          {localError}
        </p>
      ) : null}

      {showStorefrontFocal && imageUrl ? (
        <StorefrontFocalAdjuster
          imageUrl={imageUrl}
          position={position}
          onChange={onStorefrontObjectPositionChange}
        />
      ) : null}
    </div>
  );
}
